// 登录后强制改密 / 绑 2FA 守卫（改密优先）；代登录会话豁免（R86，后端已按 impersonatorAdminId 豁免强制门禁）
//
// AC-6（SPRINT-WEB-CRITICAL）：/auth/me 探测失败必须进入 error 态 + 重试，
// 业务路由一律不渲染 Outlet（唯一豁免：改密/2FA 设置页，避免「去设置」死链）。
import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { fetchMe } from '@/api/auth';
import { loadSession, saveSession, SESSION_CHANGED_EVENT } from '@/api/client';
import { isImpersonatedSession } from './impersonation';
import { canRenderOutlet, isSetupPath, phaseFromMe, type SetupPhase } from './requireSetupState';

/** 现网 App.tsx：settings/security 同时承载改密表单与 2FA */
const SETUP_PATH = '/agent/settings/security';

/** 订阅 session 写入，供布局/安全页在改密或绑 2FA 后重读标志 */
export function useSetupFlags() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(SESSION_CHANGED_EVENT, bump);
    return () => window.removeEventListener(SESSION_CHANGED_EVENT, bump);
  }, []);
  const session = loadSession();
  return {
    mustChangePassword: session?.me?.mustChangePassword === true,
    mfaSetupRequired: session?.me?.mfaSetupRequired === true,
    // 代登录会话标识：顶层 impersonated（portal-login 写入）优先，me.impersonated（契约 §0.4 R75）回落
    impersonated: isImpersonatedSession(session),
  };
}

export default function RequireSetup() {
  const location = useLocation();
  const { mustChangePassword, mfaSetupRequired, impersonated } = useSetupFlags();
  const [phase, setPhase] = useState<SetupPhase>('loading');
  /** 重试代次：自增触发 effect 重新探测 */
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetchMe();
        if (cancelled) {
          return;
        }
        if (res.ok) {
          const s = loadSession();
          if (s) {
            saveSession({ ...s, me: res.data });
          }
        }
        // ok:true → ready；ok:false（含 401/网络失败信封）→ error（绝不放行）
        setPhase(phaseFromMe(res));
      } catch {
        // 网络异常/超时与 ok:false 同口径：留在 error 态，绝不放行 Outlet
        if (!cancelled) {
          setPhase('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const retry = useCallback(() => {
    setPhase('loading');
    setAttempt((n) => n + 1);
  }, []);

  if (phase === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-slate-500">加载会话…</p>
      </div>
    );
  }

  if (!canRenderOutlet({ phase, pathname: location.pathname })) {
    // error 态：仅设置页豁免已由 canRenderOutlet 处理；其余业务路由显示错误 + 重试
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-sm font-medium text-rose-600">会话加载失败</p>
        <p className="text-xs text-slate-500 max-w-sm">
          无法确认账号安全设置，暂不能进入该页面。请检查网络后重试。
        </p>
        <button type="button" className="btn-secondary text-sm" onClick={retry}>
          重试
        </button>
      </div>
    );
  }

  // R86：代登录会话不参与强制改密/绑 2FA 跳转（该页敏感动作已被 3432 硬禁，否则成单向死页）；
  // 普通会话判定逐字保留，门禁不放松。
  if (mustChangePassword && !impersonated && !isSetupPath(location.pathname)) {
    return <Navigate to={SETUP_PATH} replace={true} />;
  }
  if (mfaSetupRequired && !impersonated && !isSetupPath(location.pathname)) {
    return <Navigate to={SETUP_PATH} replace={true} />;
  }
  return <Outlet />;
}
